import {
  ColorWheelIcon,
  Pencil2Icon,
  PlusIcon,
  TrashIcon,
} from '@radix-ui/react-icons';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { FC, useMemo } from 'react';

import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { DataTable } from 'components/ui/data-table';
import { useModal } from 'hooks/useModal';
import { usePageCarto } from 'hooks/usePageCarto';
import { PageCartoLayerForm } from './PageCartoLayerForm';
import { ContentType } from 'services/abstract';
import { useService } from 'hooks/useService';
import { useMutation, useQueryClient } from 'react-query';
import { useToast } from 'hooks/useToast';

export const PageCartoLayerPanel: FC = () => {
  const { showModal, hideModal } = useModal();
  const queryClient = useQueryClient();
  const { pageCartoId, layers: dataGridRows } = usePageCarto();
  const layerService = useService(ContentType.LAYER);
  const { addToast } = useToast();

  const { mutateAsync: deleteLayer } = useMutation({
    mutationFn: async (layerId: number) => {
      return await layerService.deleteLayer(layerId);
    },
    onSuccess: (_response) => {
      addToast({
        title: 'Contour supprimé',
        description: `Votre contour a été supprimé avec succès`,
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['page-carto', pageCartoId] });
    },
  });

  const columns = useMemo<ColumnDef<(typeof dataGridRows)[number]>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Nom',
      },
      {
        accessorKey: 'fill',
        header: 'Fond',
        cell: ({ row }) => (
          <span className="flex items-center gap-2">
            <ColorWheelIcon
              width={16}
              height={16}
              color={row.original.fill}
              className="shrink-0"
            />
            {row.original.fill}
          </span>
        ),
      },
      {
        accessorKey: 'stroke',
        header: 'Contour',
        cell: ({ row }) => (
          <span className="flex items-center gap-2">
            <Pencil2Icon
              width={16}
              height={16}
              color={row.original.stroke}
              className="shrink-0"
            />
            {row.original.stroke}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button
              icon={TrashIcon}
              size="sm"
              color="red"
              onClick={() => deleteLayer(row.original.id)}
            />
          </div>
        ),
        enableSorting: false,
        enableHiding: false,
        meta: {
          className: 'w-24 text-right',
          cellClassName: 'text-right',
        },
      },
    ],
    [deleteLayer]
  );

  const table = useReactTable({
    data: dataGridRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      <div className="mt-5 text-right">
        <Button
          icon={PlusIcon}
          data-testid="import-dataset"
          onClick={() =>
            showModal({
              title: 'Nouvelle couche',
              Component: PageCartoLayerForm,
              props: { pageCartoId, onSubmit: hideModal },
            })
          }
        >
          Nouvelle couche
        </Button>
      </div>
      <div className="my-2">
        {dataGridRows.length === 0 ? (
          <Alert variant="info">{`Aucune couche n'a été importée`}</Alert>
        ) : (
          <DataTable
            table={table}
            className="w-full max-h-96 overflow-y-auto"
          />
        )}
      </div>
    </div>
  );
};
